import { Box, Button, Heading, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { InputBuild, ModalBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { InstitutionsResponseDTO } from 'models'
import { RefObject, useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { createResponsible, editResponsible } from 'services/legal-responsible'
import { ResponsibleForm } from '../form'
import { ResponsibleSchemaForm } from '../interfaces'
import { ResponsibleKeys } from '../types'
import { schema } from './schema'

interface PropsModalResponsible {
    handleClose: (callbackGet?: boolean) => void
    refModal: RefObject<ModalBuildHandle>
    itemEdit: any
    institution: InstitutionsResponseDTO
    setItemEdit: React.Dispatch<React.SetStateAction<any>>
}

export function ModalResponsible({
    handleClose,
    refModal,
    itemEdit,
    institution,
    setItemEdit
}: PropsModalResponsible) {
    const form = useMemo(() => ResponsibleForm, [ResponsibleForm])
    const toast = useToast()
    const {
        handleSubmit,
        register,
        watch,
        reset,
        setValue,
        formState: { errors, isSubmitting }
    } = useForm<ResponsibleSchemaForm>({
        resolver: yupResolver(schema)
    })

    const hasError = (id: ResponsibleKeys) => {
        return errors && errors[id]
    }

    function emitToastError(error: any, label = 'Registrar') {
        const message =
            error?.response?.data[0]?.mensagem ??
            'Algo deu errado, tente novamente mais tarde.'

        toast({
            title: `Falha ao ${label} Responsável Legal`,
            description: message,
            position: 'bottom-left',
            status: 'error',
            isClosable: true
        })
    }

    function emitToastSuccess(label = 'cadastrado') {
        toast({
            title: `Responsável Legal`,
            description: `O responsável legal foi ${label} com sucesso!`,
            position: 'bottom-left',
            status: 'success',
            isClosable: true
        })
    }

    function formatPayloadResponsibile({
        nome,
        cpf,
        telefone,
        email,
        funcao
    }: ResponsibleSchemaForm) {
        return {
            nome,
            cpf: cpf.replace(/\D/g, ''),
            telefone: telefone.replace(/\D/g, ''),
            email,
            funcao
        }
    }

    async function fetchCreateResponsible(data: ResponsibleSchemaForm) {
        try {
            await createResponsible(
                institution?.id,
                formatPayloadResponsibile(data)
            )
            emitToastSuccess()
            handleCancel(true)
        } catch (error: unknown) {
            emitToastError(error)
        }
    }

    async function fetchEditResponsible(data: ResponsibleSchemaForm) {
        try {
            const payload = {
                idInstitution: institution?.id,
                data: formatPayloadResponsibile(data),
                idResponsible: itemEdit?.id
            }
            await editResponsible(payload)
            emitToastSuccess('editado')
            handleCancel(true)
        } catch (error: unknown) {
            emitToastError(error, 'Editar')
        }
    }

    const handleFormSubmit = async (data: ResponsibleSchemaForm) => {
        if (!itemEdit?.id) await fetchCreateResponsible(data)
        else await fetchEditResponsible(data)
    }

    const handleCancel: any = (isSave = false) => {
        reset()
        handleClose(isSave)
    }

    useEffect(() => {
        if (itemEdit?.id) {
            Object.keys(itemEdit).forEach((field: any) => {
                const newValue = itemEdit[field]
                setValue(field, newValue)
            })
        }
    }, [itemEdit, refModal])

    const handleReset = () => {
        reset()
        setItemEdit({})
    }

    return (
        <>
            <ModalBuild
                ref={refModal}
                closeOnOverlayClick={false}
                size="xl"
                title=" "
                onCancel={() => handleReset()}
            >
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <Layout type="container" px={8} full display="grid" gap={8}>
                        <Layout type="row">
                            <Box>
                                <Heading
                                    display="flex"
                                    size="md"
                                    alignItems="center"
                                    justifyContent={{
                                        base: 'space-between',
                                        sm: 'center'
                                    }}
                                >
                                    {itemEdit?.nome ? 'Editar' : 'Adicionar'}{' '}
                                    Responsável Legal
                                </Heading>
                            </Box>
                        </Layout>
                        <Layout type="row" gutter={{ y: 1.5 }} mb={6}>
                            {form.map(({ layout, input }, key) => (
                                <Layout {...layout} key={key}>
                                    {input && (
                                        <InputBuild
                                            {...input}
                                            id={input.id}
                                            err={hasError(input.id)}
                                            {...register(input.id)}
                                            value={watch(input.id) as string}
                                        />
                                    )}
                                </Layout>
                            ))}{' '}
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                gap={6}
                                mt={8}
                            >
                                <Button
                                    colorScheme="primary"
                                    variant="outline"
                                    onClick={handleCancel}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    colorScheme="primary"
                                    type="submit"
                                    isLoading={isSubmitting}
                                >
                                    Salvar
                                </Button>
                            </Box>
                        </Layout>
                    </Layout>
                </form>
            </ModalBuild>
        </>
    )
}
